import * as Util from "../common/util";
import * as Data from "../interfaces/data";
import { ITranslatedText } from "../interfaces/translations";
import { ITargetLinkedCustomerDocument, ITargetMaster } from "./targetData";
import { ITemplateLinkedCustomerDocument } from "./templateData";

export interface IAppointmentAdditionalFormPicture {
    /// unique ID for this picture
    ID: number;

    /// the comment this picture is associated with
    AppointmentAdditionalFormCommentID?: number;

    /// a unique ID for the picture used when the picture is first downloaded
    PictureID: string;

    /// the path to the picture file
    Picture: string;
}

export interface IAppointmentAdditionalDocument {
    /// unique ID for the additional document.
    ID: number;
    FileName: string;
    Description: string;
    FileUniqueKey: string;
    StorageKey: string;
}

export interface IAppointmentFormItemLinkedCustomerDocument {
    AppointmentFormItemCustomerDocumentID: number;
    CustomerDocumentID: number;
    Description: string;
    FileName: string;
}

export interface IAppointmentAdditionalFormAnswer {
    /// unique ID for the answers.
    /// required for saving if updating an existing answer
    ID: number;

    /// the answer entered by the user.
    /// required for save.
    Value: string;
}

export interface ISaveFormItems {
    // form Items to be saved
    FormItems: IAppointmentFormItem[];

    // errors if any to be corrected before save
    errors: any;
}

export interface IAppointmentAdditionalFormComment {
    /// appointmentAdditionalFormComment.ID
    ID: number;

    /// the comment text
    Comment: string;

    /// pictures associated with this comment
    Pictures: IAppointmentAdditionalFormPicture[];
}

export interface IAppointmentAdditionalFormItem {
    /// the template item associated with this form instance
    TemplateFormItemID: number;

    /// the answer or answers for this single field
    Answers: IAppointmentAdditionalFormAnswer[];

    /// used when the control type is comment or comment/pictures.
    /// hold the comments entered by the user.
    Comments: IAppointmentAdditionalFormComment[];

    /// used when the control type is document.
    Documents: IAppointmentAdditionalDocument[];
}

export interface IAppointmentFormItemDocumentTypeAllowed {
    ID: KnockoutObservable<number>;
    AppointmentID: KnockoutObservable<number>;
    AppointmentFormItemID: KnockoutObservable<number>;
    DocumentFileTypeAllowedID: KnockoutObservable<number>;
    DocumentTypeExtension: KnockoutObservable<string>;
}
export interface IAppointmentFormAnswer {
    /// unique ID for the answers.
    /// required for saving if updating an existing answer
    ID: number;

    /// the answer entered by the user.
    /// required for save.
    Value: string;
}
export interface IAppointmentDocument {
    /// Unique ID for Document
    ID: number;
    TempID: number;
    FileName: string;
    Description: string;
    FileUniqueKey: string;
    StorageKey: string;
    IsUpload: boolean;
}

export interface IAppointmentPicture {
    /// unique ID for the picture
    /// required for save if updating an existing picture
    ID: number;
    TempID: number;

    /// a unique that is assigned to the picture by the device that took the picture.
    /// needed because pictures are saved to the DB before the appointment.  This ID
    /// is used to find those saved pictures and associate them with the appointment.
    /// required for save.
    PictureID: string;

    /// the path used to retrieve the picture.
    StorageKey: string;

    IsUpload: boolean;
}

export interface IAppointmentTempDocument {
    /// unique ID for the picture
    /// required for save if updating an existing picture
    ID: number;

    /// a unique that is assigned to the picture by the device that took the picture.
    /// needed because pictures are saved to the DB before the appointment.  This ID
    /// is used to find those saved pictures and associate them with the appointment.
    /// required for save.
    FileUniqueKey: string;

    /// the path used to retrieve the picture.
    StorageKey: string;
    FileName: string;
}

export interface IAppointmentComment {
    /// unique ID for this comment
    /// required for save if updating an existing comment
    ID: number;

    /// the comment entered by the user
    /// required for save
    Comment: string;

    /// any pictures associated with this comment
    /// required for save if this comment has pictures
    Pictures: IAppointmentPicture[];
}

export interface IAppointmentListItem {
    /// the ID of the list item
    ID: number;

    /// the ID of the TemplateForm that can be added
    TemplateFormID: number;

    /// the parentID of the list item
    ParentID: number;

    /// the display name for the template form
    ListItemName: string;

    /// the order in which to display the available forms
    SortOrder: number;
}

export interface IAppointmentField {
    ID: number;
    AppointmentFormItemID: number;
    HasException: boolean;
    HasExceptionForm: boolean;
    FieldText: string;
    FieldValue: string;
    SortOrder: number;
    IsActive: boolean;
    TemplateFormID: boolean | null;
}

export interface IAppointmentControlTypeAttribute {
    ID: number;
    MinVal: string;
    MaxVal: string;
    Format: string;
    MinLength: number;
    MaxLength: number;
    Mask: string;
    NumDecimals: number;
    NumericDefaultValue: number;
    Interval: number;
}

export interface ITemplatePhraseDetail {
    /// unique ID for this field.
    ID: number;
    Phrase: string;
    SortOrder: number;
}

export interface IAppointmentFormItem {
    // fields Required When Saving

    /// appointmentFormItemID
    /// required when saving, if updating an existing item
    ID: number;

    /// the template form item this item is associated with
    /// required for saving.
    TemplateFormItemID: number;

    /// the appointmentID this item is associated with
    AppointmentID: number;

    /// the answer or answers entered by the user
    /// required for saving (for all controls except Comment)
    FormAnswer: IAppointmentFormAnswer[];

    /// if the control is a comment control, this fields contains
    /// the comments entered by the user
    /// required for saving if control type is Comment
    Comments: IAppointmentComment[];

    Documents: IAppointmentDocument[];

    /// contains answers for the additional form associated with this item.
    /// required for save if AdditionalTemplateFormID has a value.
    AdditionalFormItems: IAppointmentAdditionalFormItem[];

    // -------------
    // static Fields

    /// the type of control (text box, drop down, options buttons, etc.)
    ControlTypeID?: number;

    /// the label to display with the control.
    FormText?: string;

    /// Language translations for FormText
    FormTextTranslations?: ITranslatedText[];

    /// used to order the display of the controls
    SortOrder?: number;

    /// if true, the user must enter a value
    IsRequired?: boolean;

    /// if true, the user cannot change the value
    IsReadOnly?: boolean;
    IsVisible: boolean;
    MultipleAdditionalForms: boolean;
    /// possible values that can be selected for this field.
    /// used with drop down lists, check boxes, options buttons, etc.
    Fields?: IAppointmentField[];

    /// information used to validate the user input, like max length, min/max values, etc.
    AppointmentControlTypeAttribute?: IAppointmentControlTypeAttribute;

    /// to hold the information of type of documents allowed
    AppointmentFormItemDocumentTypesAllowed?: IAppointmentFormItemDocumentTypeAllowed[];

    /// reference to a list of standard phrases, if one is used with this control
    TemplatePhraseID?: number;

    /// template phrases that are associated with certain control types
    /// used to populate drop down with common phrases for text boxes, text area and comments
    TemplatePhrases?: ITemplatePhraseDetail[];

    TargetTemplateID?: number;

    Targets: ITargetMaster[];

    /// the additional form that is associated with this item.
    /// this is optional, not all items have an additional form
    AdditionalTemplateFormID?: number;

    // answer
    selected?: string;

    // answer
    dropdownselected?: string | null;

    // answer Array for check box lists
    selectedArray?: string[];

    IsAdditionalForm?: boolean;

    AppointmentFormItemLinkedCustomerDocuments?: IAppointmentFormItemLinkedCustomerDocument[];
}

export interface ISaveAppointmentResponse {
    ID: number;
    AppointmentAlreadyUploaded: boolean;
}

export interface IAppointmentList {
    // fields Required For Save

    /// unique ID for this list item
    /// required for save
    ID: number;

    ParentID?: number;

    NavigationID: number;
    /// lists that are children of this list.  These are lists that get
    /// created automatically when an appointment is created from
    /// this template.
    /// required for save when HasForm is false
    Children: IAppointmentList[];

    /// the fields in the attribute form associated with this list item
    /// required for save when HasAttributes is true
    AttributeForm: IAppointmentFormItem[];

    /// if this list item is a form, this is a reference to the template form
    /// that describes how the form should look.
    /// if this field is null, then this list item goes to another list.
    /// required for save when HasForm is true
    TemplateFormID?: number;

    /// if this list item is a form, contains the fields for the form
    /// required for save when HasForm is true.
    ListForm: IAppointmentFormItem[];

    /// the display name for the list item
    /// required for save
    ListName: string;

    // if false, then the item has been deleted
    IsActive: boolean;

    // ---------------
    // static Fields

    /// if true, then this list item is a form.
    /// if false, then this is a list that contains children items
    HasForm: boolean;

    /// if true, then this list item has an attribute form
    HasAttributes: boolean;

    /// the order to display the list items
    SortOrder: number;

    /// if true, the user can delete this list item, or change its name.
    /// this will be false for any list items that are part of the template
    CanDelete: boolean;

    /// these are forms that can be added to this list.
    /// only used when HasForm is false
    ListItems: IAppointmentListItem[];
}

export interface IAppointmentNavigation {
    // fields Required for Save

    /// the Unique ID for this appointment navigation.
    /// required when saving.
    ID: number;

    /// the list items that exist under this navigation
    /// only used when the navigation type is 'List'
    /// required when saving (if type is list)
    Lists: IAppointmentList[];

    /// used when a navigation item goes directly to a form (TemplateFormID is not null).
    /// contains all of the fields on that form.
    /// only used when navigation type is 'Form'
    /// required when saving (if type is form)
    Forms: IAppointmentFormItem[];

    // -----------------
    // static Fields

    /// indicates what kind of item this navigation goes to, such
    /// as form, list or report.
    NavigationTypeID: number;

    /// if this field contains a value, then this navigation item
    /// goes directly to a form.
    /// if null, then navigation item goes to a list or exception report.
    TemplateFormID: number | null;

    /// the name of the navigation to display in the UI
    NavigationName: string;

    /// Translations for NavigationName
    NavigationNameTranslations: ITranslatedText[];

    /// the icon to use in the UI
    NavigationIcon: string | null;

    /// the order of the navigation items
    SortOrder: number;

    /// a list of form templates that can be created in this list.
    /// only used when the navigation type is 'List'
    ListItems: IAppointmentListItem[];
}

export interface IAppointmentTargetFormItem {
    /// unique ID for this value
    ID: number;

    /// the label for the field
    FormText: string;

    /// the value of this fields
    Value: string;

    /// identifies what form field is associated with this item
    TargetTemplateFormItemID: number;

    ControlTypeID: number;
}

export interface IAppointmentTarget {
    TargetTemplateID: number;
    Address: string;
    Address2: string;
    City: string;
    State: string;
    Zip: string;
    County: string;
    Country: string;
    Phone: string;
    /// any custom fields associated with this target
    FormItems: IAppointmentTargetFormItem[];
    // the type of target (location or custom)
    TargetTemplateTypeID: number;
    LinkedCustomerDocuments: ITargetLinkedCustomerDocument[];
}

export interface IAppointmentTemplateFormItem {
    /// appointmentFormItemID
    ID: number;

    /// the template form associated with this item
    TemplateFormID: number;

    /// the template form item this item is associated with
    TemplateFormItemID: number;

    /// the type of control (text box, drop down, options buttons, etc.)
    ControlTypeID: number;

    /// the label to display with the control.
    FormText: string;

    /// used to order the display of the controls
    SortOrder: number;

    /// if true, the user must enter a value
    IsRequired: boolean;

    /// if true, the user cannot change the value
    IsReadOnly: boolean;

    /// possible values that can be selected for this field.
    /// used with drop down lists, check boxes, options buttons, etc.
    Fields: IAppointmentField[];

    /// possible values that can be selected for this field.
    /// used with drop down lists, check boxes, options buttons, etc.
    FormAnswer: IAppointmentFormAnswer[];

    /// information used to validate the user input, like max length, min/max values, etc.
    AppointmentControlTypeAttribute: IAppointmentControlTypeAttribute;

    /// Used to hold the DocumentFileTypesAllowedForTheFormItem,if the control type is Document

    AppointmentFormItemDocumentTypesAllowed: IAppointmentFormItemDocumentTypeAllowed[];

    /// reference to a list of standard phrases, if one is used with this control
    TemplatePhraseID?: number;

    /// the additional form that is associated with this item.
    /// this is optional, not all items have an additional form
    AdditionalTemplateFormID?: number;

}
export interface IAppointmentDetail {
    /// unique ID for this appointment.
    /// required for save when updating an existing appointment
    ID: number;

    AppointmentDate: any;

    StatusID: number;

    UserID?: number;
}

export interface IAppointment {
    /// unique ID for this appointment.
    /// required for save when updating an existing appointment
    ID: number;

    /// the ID of the template this appointment is based on.
    /// required for save when creating a new appointment.
    TemplateID: number;

    /// the user assigned to perform this appointment
    UserID: number;

    /// the ID of an existing target associated with this appointment.
    /// if set to 0, then the Target property must be populated
    /// with information to create a new target.
    TargetID: number;

    TemplateName: string;

    /// the date and time of the appointment, in UTC time.
    AppointmentDate?: string;

    Timeframe: number;

    /// the status of the appointment.
    StatusID: number;

    /// the top level navigation items for this appointment
    Navigations: IAppointmentNavigation[];

    /// used to create or update a target.
    /// can be null if TargetID is set to a value.
    Target: IAppointmentTarget;

    /// definition for form templates that have been copied into this appointment.
    /// used to keep the version of the template as it existed when the appointment
    /// was performed.
    /// read-only data, does not update the TemplateFormItem table.
    TemplateFormItems: IAppointmentTemplateFormItem[];

    LinkedCustomerDocuments: ITemplateLinkedCustomerDocument[];

    ReportID?: number;
    ReportRecipients: string[];

    /// The language translations to use for the labels when displaying the task
    LanguageID: number;
}

export interface IRelatedAppointment {
    RootID: number;
    AppointmentID: number;
    ParentID?: number;
    DateTimeCreated: string;
}

export interface IAppointmentStatusType {
    ID: number;
    Name: string;
    IsOpen: boolean;
    IsCompleted: boolean;
    IsCancelled: boolean;
}

export interface IAppointmentMaster {
    /// the data for the appointment
    Appointment: IAppointment;

    /// all possible status values that an appointment can be assigned
    StatusValues: IAppointmentStatusType[];

    // all users who can perform an appointment
    AppointmentUsers: Array<Data.ICodeModel<number>>;
    ReportEmailLogs: IReportEmailLog[];
    RelatedAppointments: IRelatedAppointment[];
}

export interface IReportEmailLog {
    ID: number;
    ReportID: number;
    ReportName: string;
    CreatedBy: string;
    DateTimeCreated: string;
    EmailAddress: string;
}

export interface IAppointmentExceptionReportNavigation {
    AppointmentID: number;
    Name: string;
    SortOrder: number;

    FormItems: IAppointmentExceptionReportFormItem[];
    ListForms: IAppointmentExceptionReportListForm[];
}

export interface IAppointmentExceptionReport {
    ExceptionReportNavigations: IAppointmentExceptionReportNavigation[];
}

export interface IAppointmentExceptionReportListForm {
    ParentListName: string;
    ListName: string;

    FormItems: IAppointmentExceptionReportFormItem[];
}

export interface IAppointmentExceptionReportFormItem {
    AppointmentFormItemID: number;
    FormText: string;

    Answers: IAppointmentExceptionReportAnswer[];
    AdditionalFormItems: IAppointmentExceptionReportAdditionalFormItem[];
}

export interface IAppointmentExceptionReportAdditionalFormItem {
    AppointmentFormItemID: number;
    OrigAppointmentFormItemID: number;

    ParentFormText: string;
    FormText: string;

    Answers: IAppointmentExceptionReportAnswer[];
}

export interface IAppointmentExceptionReportAnswer {
    AnswerID: number;
    AppointmentFormItemID: number;

    FieldText: string;
    Value: string;
}

export interface IAppointmentLocationLog {
    ID: number;
    AppointmentID: number;
    RecordedDateTime: string;
    Latitude: number;
    Longitude: number;
    Description: string;
}

export function GetMaster(id: number): JQueryDeferred<Data.IResponse<IAppointmentMaster>> {
    return Util.callWebService(
        "/api/Appointment/GetAppointmentByID/" + id,
        "GET");
}

export interface IGetFormItemResponse {
    AppointmentFormItem: IAppointmentFormItem[];
}

export function GetFormItemsByTemplateFormID(templateFormID: number | null, appointmentID: number,
    navigationID: number, origFormItemID?: number, listID?: number | null):
    JQueryDeferred<Data.IResponse<IGetFormItemResponse>> {
    return Util.callWebService(
        "/api/Appointment/GetFormItemsByTemplateFormID",
        "POST",
        {
            appointmentID,
            listID,
            navigationID,
            origFormItemID,
            templateFormID,
        });
}

export interface IGetAdditionalFormItemResponse {
    AppointmentFormItem: IAppointmentFormItem[];
}

export function GetAdditionalFormItemsByTemplateFormID(templateFormID: number | null, appointmentID: number,
    navigationID: number, origFormItemID?: number, listID?: number | null):
    JQueryDeferred<Data.IResponse<IGetAdditionalFormItemResponse>> {
    return Util.callWebService(
        "/api/Appointment/GetAdditionalFormItemsByTemplateFormID",
        "POST",
        {
            appointmentID,
            listID,
            navigationID,
            origFormItemID,
            templateFormID,
        });
}

export interface ISaveAppointmentDetailRequest {
    appointmentID: number;
    appointmentDate: any;
    statusID: number;
    userID?: number;
    languageID: number;
}

export function SaveAppointmentDetail(request: ISaveAppointmentDetailRequest): JQueryDeferred<Data.IResponse<IAppointmentDetail>> {
    return Util.callWebService(
        "/api/Appointment/SaveAppointmentDetail",
        "POST",
        request);
}

export function SaveAppointmentForm(appointmentFormItems: IAppointmentFormItem[], navigationID: number,
    skipValidation: boolean, listID?: number | null): JQueryDeferred<Data.IResponse<ISaveAppointmentResponse>> {
    return Util.callWebService(
        "/api/Appointment/SaveAppointmentFormItemAnswers",
        "POST",
        {
            appointmentFormItems,
            listID,
            navigationID,
            skipValidation,
        });
}

export function SaveAppointmentAdditionalForm(appointmentFormItems: IAppointmentFormItem[], navigationID: number,
    listID?: number | null, origFormItemID?: number):
    JQueryDeferred<Data.IResponse<ISaveAppointmentResponse>> {
    return Util.callWebService(
        "/api/Appointment/SaveAppointmentAdditionalFormItemAnswers",
        "POST",
        {
            appointmentFormItems,
            listID,
            navigationID,
            origFormItemID,
        });
}

export function SaveNewListForm(listName: string, ID: number, appointmentID: number,
    navigationID: number): JQueryDeferred<Data.IResponse<IAppointmentList>> {
    return Util.callWebService(
        "/api/Appointment/SaveNewListForm",
        "POST",
        {
            appointmentID,
            listItemID: ID,
            listName,
            navigationID,
        });
}

export function SaveListOrder(lists: any): JQueryDeferred<Data.IResponse<any>> {
    return Util.callWebService(
        "/api/Appointment/SaveListOrder",
        "POST",
        {
            lists,
        });
}

export function GetUploadedFile(id: string): JQueryDeferred<Data.IResponse<IAppointmentTempDocument>> {
    return Util.callWebService(
        "/api/FileUploadApi/GetUploadedFile/" + id,
        "GET");
}

export function DeletePictures(pictureType: string, pictureID: number): JQueryDeferred<Data.IResponse<boolean>> {
    return Util.callWebService(
        "/api/AppointmentPicture/delete/" + pictureType + "/" + pictureID,
        "POST");
}

export function GetExceptionReportData(appointmentID: number): JQueryDeferred<Data.IResponse<IAppointmentExceptionReport>> {
    return Util.callWebService(
        "/api/Appointment/GetExceptionReportData",
        "POST",
        appointmentID);
}

export function GetLocationLogs(appointmentID: number): JQueryDeferred<Data.IResponse<IAppointmentLocationLog[]>> {
    return Util.callWebService(
        "/api/Appointment/GetLocationLogs/" + appointmentID,
        "GET");
}

export function CloneAppointment(appointmentID: number): JQueryDeferred<Data.IResponse<number>> {
    return Util.callWebService(
        "/api/Appointment/Clone/" + appointmentID,
        "POST");
}

export interface IAppointmentChangeStatusRequest {
    AppointmentID: number;
}

export function CompleteAppointment(appointmentID: number): JQueryDeferred<Data.IResponse<boolean>> {
    const request: IAppointmentChangeStatusRequest = {
        AppointmentID: appointmentID,
    };
    return Util.callWebService(
        "/api/Appointment/CompleteAppointment",
        "POST",
        request);
}

export function CancelAppointment(appointmentID: number): JQueryDeferred<Data.IResponse<any>> {
    const request: IAppointmentChangeStatusRequest = {
        AppointmentID: appointmentID,
    };
    return Util.callWebService(
        "/api/Appointment/CancelAppointment",
        "POST",
        request);
}

export function OpenAppointment(appointmentID: number): JQueryDeferred<Data.IResponse<any>> {
    const request: IAppointmentChangeStatusRequest = {
        AppointmentID: appointmentID,
    };
    return Util.callWebService(
        "/api/Appointment/OpenAppointment",
        "POST",
        request);
}

// ------------------------------------------------------
// Dashboard data
// ------------------------------------------------------
export interface IAppointmentCountDashboardItem {
    AppointmentTemplateName: string;
    AppointmentDate: string;
    AssignedUserName: string;
    State: string;
    NumberOfAppointments: string;
}

export interface IAppointmentCountForDashboardRequest {
    startDate: string;
    endDate: string;
}

export function GetForDashboardCount(request: IAppointmentCountForDashboardRequest): JQueryDeferred<Data.IResponse<IAppointmentCountDashboardItem[]>> {
    return Util.callWebService(
        "/api/Appointments/ForDashboardCount",
        "POST",
        request,
        false);
}

// ------------------------------------------------------
// Appointment/Application Validation
// ------------------------------------------------------

export interface IAppointmentValidationErrors {
    NavigationID: number;
    NavigationName: string;
    Errors: string[];
}

export interface IAppointmentValidationErrorsResponse {
    NavigationErrors: IAppointmentValidationErrors[];
}

export function ValidateAppointment(appointmentID: number): JQueryDeferred<Data.IResponse<IAppointmentValidationErrorsResponse>> {
    return Util.callWebService(
        "/api/Appointment/ValidateTask",
        "POST",
        appointmentID);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/data/appointmentData.ts